import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import './HomePage.css';

const features = [
    {
        icon: '🧠',
        title: 'Active recall, not passive reading',
        desc: 'Type your answer before seeing options. Forcing retrieval beats re-reading every time — that\'s the science.',
    },
    {
        icon: '📊',
        title: 'Confidence-weighted practice',
        desc: 'Rate how well you know each answer. The app surfaces questions where your confidence is lowest.',
    },
    {
        icon: '🤖',
        title: 'AI-generated questions on demand',
        desc: 'Never run out of material. Generate fresh, relevant practice questions for any topic in your chosen certification.',
    },
    {
        icon: '🎥',
        title: 'Curated further learning',
        desc: 'Every question links to YouTube videos, a hands-on lab guide, and official vendor documentation.',
    },
    {
        icon: '📅',
        title: 'Exam date tracking',
        desc: 'Set a goal date and see exactly how many days you have left. Keeps your study sessions purposeful.',
    },
    {
        icon: '✅',
        title: 'Instant answer grading',
        desc: 'Your free-text answer is graded against the correct answer automatically. No waiting, no guessing.',
    },
];

const flowSteps = [
    { label: '01', title: 'Pick a cert', desc: 'Choose from the available certifications' },
    { label: '02', title: 'Answer freely', desc: 'Type your answer before seeing choices' },
    { label: '03', title: 'Get graded', desc: 'AI scores your response instantly' },
    { label: '04', title: 'Rate confidence', desc: 'Log how well you knew the answer' },
    { label: '05', title: 'Learn deeper', desc: 'Watch, read, and practice more' },
];

function DailyQuestionSection() {
    const [dailyQ, setDailyQ] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/daily-question`)
            .then(r => r.json())
            .then(data => {
                setDailyQ(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    if (loading || !dailyQ) return null;

    const options = dailyQ.options;
    const optionEntries = Array.isArray(options)
        ? options.map((text, i) => [String.fromCharCode(65 + i), text])
        : Object.entries(options);

    // correct_answer may be a letter key ("A") or the full option text — handle both
    const correctKey = (
        optionEntries.find(([k, v]) => k === dailyQ.correct_answer || v === dailyQ.correct_answer)?.[0]
        ?? dailyQ.correct_answer
    );

    function getOptionClass(key) {
        if (!selectedAnswer) return 'hp-daily-option';
        if (key === correctKey) return 'hp-daily-option correct';
        if (key === selectedAnswer) return 'hp-daily-option incorrect';
        return 'hp-daily-option dimmed';
    }

    const incorrectExplanations = dailyQ.incorrect_explanations || {};

    return (
        <>
            <hr className="home-section-divider" />
            <div className="hp-daily-section">
                <div className="hp-daily-header-row">
                    <span className="hp-daily-badge">Daily Question</span>
                    {/* `generation_paused` comes from /api/daily-question. While the
                        kill switch is on, nothing new is being made, so the usual
                        promise would be a claim this page cannot keep. */}
                    <span className="hp-daily-refresh-note">
                        {dailyQ.generation_paused
                            ? 'Fresh questions are paused right now'
                            : '↻ New question generated every day'}
                    </span>
                </div>
                <div className="hp-daily-card">
                    <p className="hp-daily-breadcrumb">
                        {dailyQ.domain_name} &rsaquo; {dailyQ.group_name} &rsaquo; <strong>{dailyQ.topic_name}</strong>
                    </p>
                    <p className="hp-daily-question">{dailyQ.question_text}</p>
                    <div className="hp-daily-options">
                        {optionEntries.map(([key, text]) => (
                            <button
                                key={key}
                                className={getOptionClass(key)}
                                onClick={() => { if (!selectedAnswer) setSelectedAnswer(key); }}
                                disabled={!!selectedAnswer}
                            >
                                <span className="hp-daily-option-key">{key}</span>
                                <span className="hp-daily-option-text">{text}</span>
                            </button>
                        ))}
                    </div>
                    {selectedAnswer && (
                        <div className="hp-daily-explanation">
                            {selectedAnswer === correctKey ? (
                                <p className="hp-daily-result correct-result">Correct!</p>
                            ) : (
                                <p className="hp-daily-result incorrect-result">
                                    Incorrect — the correct answer is <strong>{correctKey}</strong>.
                                    {incorrectExplanations[selectedAnswer] && (
                                        <> {incorrectExplanations[selectedAnswer]}</>
                                    )}
                                </p>
                            )}
                            <p className="hp-daily-explanation-text">{dailyQ.explanation}</p>
                        </div>
                    )}
                    <div className="hp-daily-cta-row">
                        <Link to="/study" className="hp-daily-cta">Study more like this &rarr;</Link>
                    </div>
                </div>
            </div>
        </>
    );
}

function HomePage() {
    return (
        <div className="home-page">

            {/* Hero */}
            <div className="home-hero">
                <div className="home-hero-inner">
                    <p className="home-hero-eyebrow">
                        <span className="home-hero-eyebrow-dot" />
                        AI-powered cert prep
                    </p>
                    <h1 className="home-hero-headline">
                        Stop re-reading.<br />
                        Start <em>remembering</em>.
                    </h1>
                    <p className="home-hero-sub">
                        Practice questions generated by AI, graded instantly,
                        calibrated to your confidence — for the certifications that matter.
                    </p>
                    <div className="home-hero-actions">
                        <Link to="/study" className="home-btn-primary">
                            Start studying →
                        </Link>
                        <Link to="/about" className="home-btn-secondary">
                            How it works
                        </Link>
                    </div>
                </div>
            </div>

            {/* Daily Question */}
            <DailyQuestionSection />

            <hr className="home-section-divider" />

            {/* Features */}
            <div className="home-section">
                <p className="home-section-label">Features</p>
                <h2 className="home-section-title">Everything you need to pass</h2>
                <div className="home-features">
                    {features.map((f, i) => (
                        <div className="home-feature-card" key={i}>
                            <div className="home-feature-icon-wrap">{f.icon}</div>
                            <p className="home-feature-title">{f.title}</p>
                            <p className="home-feature-desc">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            <hr className="home-section-divider" />

            {/* Study flow */}
            <div className="home-section">
                <p className="home-section-label">The method</p>
                <h2 className="home-section-title">A study session in five steps</h2>
                <div className="home-flow">
                    {flowSteps.map((s, i) => (
                        <div className="home-flow-step" key={i}>
                            <div className="home-flow-badge">{s.label}</div>
                            <p className="home-flow-step-title">{s.title}</p>
                            <p className="home-flow-step-desc">{s.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* CTA */}
            <div className="home-cta">
                <div className="home-cta-inner">
                    <h2 className="home-cta-title">Ready to start?</h2>
                    <p className="home-cta-sub">
                        Pick a certification and run through a few questions.
                        No setup, no credit card.
                    </p>
                    <Link to="/study" className="home-btn-primary">
                        Open the study page →
                    </Link>
                </div>
            </div>

            {/* Footer */}
            <footer className="home-footer">
                <div className="home-footer-inner">
                    <Link to="/" className="home-footer-logo">
                        <span className="home-footer-logo-cert">CERT</span>
                        <span className="home-footer-logo-students">DRILL</span>
                    </Link>
                    <ul className="home-footer-links">
                        <li><Link to="/about">About</Link></li>
                        <li><Link to="/study">Study</Link></li>
                        <li><Link to="/login">Sign In</Link></li>
                        <li><Link to="/privacy">Privacy</Link></li>
                    </ul>
                    <p className="home-footer-copy">© {new Date().getFullYear()} CertDrill</p>
                </div>
            </footer>

        </div>
    );
}

export default HomePage;
